import React, { useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import {
  FiArrowRight, FiCalendar, FiStar, FiZap
} from 'react-icons/fi';
import { FaInstagram } from 'react-icons/fa';
import Particles from 'react-tsparticles';
import { loadFull } from 'tsparticles';
import SocialFloatingButton from './SocialFloatingButton';
import NarniaTalkWidget from './NarniaTalkWidget';
import AdultHeader from './AdultHeader';
import { aiToolsInfo } from '../data';

// ── Keyframes ────────────────────────────────────────────────
const moveRoad = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: 0 60px; }
`;

const auroraFlow = keyframes`
  0%   { transform: translate(-20%, -20%) scale(1);   rotate: 0deg; }
  50%  { transform: translate(10%,  10%)  scale(1.1); rotate: 180deg; }
  100% { transform: translate(-20%, -20%) scale(1);   rotate: 360deg; }
`;

const fall = keyframes`
  0%   { transform: translateY(-100%); opacity: 0; }
  10%  { opacity: 0.5; }
  90%  { opacity: 0.5; }
  100% { transform: translateY(100vh);  opacity: 0; }
`;

const floatCode = keyframes`
  0%   { transform: translateY(0)      translateX(0);   opacity: 0; }
  20%  { opacity: 0.15; }
  80%  { opacity: 0.15; }
  100% { transform: translateY(-100px) translateX(20px); opacity: 0; }
`;

const moveGradient = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 30px rgba(160, 68, 255, 0.3); }
  50%       { box-shadow: 0 0 60px rgba(160, 68, 255, 0.6); }
`;

const shine = keyframes`
  0%   { left: -100%; }
  20%  { left: 100%; }
  100% { left: 100%; }
`;

// ── Layout ───────────────────────────────────────────────────
const Page = styled.div`
  background-color: #010208;
  color: #fff;
  font-family: "Pretendard", -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  min-height: 100vh;
`;

// ── Hero Background ───────────────────────────────────────────
const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding: 160px 0 120px;
  background-color: #010208;
  text-align: center;
`;

const BackgroundLayer = styled.div`
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  z-index: 0;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 0; left: 0; width: 100%; height: 100%;
    background-image: url('https://www.transparenttextures.com/patterns/carbon-fibre.png');
    opacity: 0.12;
    mix-blend-mode: overlay;
  }
`;

const Aurora = styled.div`
  position: absolute;
  width: 150%; height: 150%;
  top: -25%; left: -25%;
  background:
    radial-gradient(circle at 20% 30%, rgba(79, 70, 229, 0.3)  0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(160, 68, 255, 0.25) 0%, transparent 50%),
    radial-gradient(circle at 50% 80%, rgba(0,  210, 255, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 10% 90%, rgba(160, 68, 255, 0.12) 0%, transparent 40%);
  filter: blur(140px);
  animation: ${auroraFlow} 25s infinite alternate ease-in-out;
  opacity: 0.85;
`;

const NeonGrid = styled.div`
  position: absolute;
  bottom: -30%; left: -50%; width: 200%; height: 100%;
  background-image:
    linear-gradient(rgba(160, 68, 255, 0.25) 1.5px, transparent 1.5px),
    linear-gradient(90deg, rgba(160, 68, 255, 0.25) 1.5px, transparent 1.5px);
  background-size: 60px 60px;
  transform: perspective(1000px) rotateX(75deg);
  animation: ${moveRoad} 2s linear infinite;
  mask-image: linear-gradient(to top, rgba(0,0,0,1) 5%, transparent 85%);
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    top: 0; left: 0; width: 100%; height: 100%;
    background-image: radial-gradient(circle at center, rgba(160, 68, 255, 0.35) 1px, transparent 0);
    background-size: 60px 60px;
    opacity: 0.5;
  }
`;

const CRTOverlay = styled.div`
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  background:
    linear-gradient(rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.12) 50%),
    linear-gradient(90deg, rgba(160, 68, 255, 0.02), rgba(0, 0, 0, 0.01), rgba(160, 68, 255, 0.02));
  background-size: 100% 3px, 3px 100%;
  z-index: 5;
  pointer-events: none;
  opacity: 0.4;
`;

const DataLine = styled.div`
  position: absolute;
  width: 1px;
  height: ${p => p.height || '150px'};
  background: linear-gradient(to bottom, transparent, ${p => p.color || '#a044ff'}, transparent);
  left: ${p => p.left};
  top: -200px;
  animation: ${fall} ${p => p.duration || '5s'} linear infinite;
  animation-delay: ${p => p.delay || '0s'};
  opacity: 0.4;
  z-index: 0;
`;

const CodeFragment = styled.div`
  position: absolute;
  font-family: 'Fira Code', monospace;
  font-size: 0.85rem;
  color: #c4b5fd;
  opacity: 0;
  white-space: nowrap;
  user-select: none;
  z-index: 1;
  animation: ${floatCode} ${p => p.duration || '10s'} linear infinite;
  animation-delay: ${p => p.delay || '0s'};
  left: ${p => p.left};
  top: ${p => p.top};
`;

// ── Hero Content ──────────────────────────────────────────────
const HeroLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  background: rgba(160, 68, 255, 0.1);
  border: 1px solid rgba(160, 68, 255, 0.4);
  border-radius: 999px;
  padding: 0.5rem 1.4rem;
  color: #c4b5fd;
  font-size: 0.85rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 2.5rem;
  box-shadow: 0 0 20px rgba(160, 68, 255, 0.2);
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.8rem, 6vw, 5rem);
  font-weight: 950;
  line-height: 1.1;
  letter-spacing: -0.04em;
  margin-bottom: 2rem;
  color: #fff;

  .accent {
    background: linear-gradient(135deg, #fff 30%, #a044ff 70%, #00d2ff 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    filter: drop-shadow(0 0 30px rgba(160, 68, 255, 0.5));
  }

  @media (max-width: 768px) { font-size: 2.5rem; }
`;

const HeroSub = styled.p`
  font-size: 1.3rem;
  color: #cbd5e1;
  max-width: 640px;
  margin: 0 auto 1.5rem;
  line-height: 1.8;
  font-weight: 600;

  strong { color: #fff; font-weight: 800; }

  @media (max-width: 768px) { font-size: 1.1rem; }
`;

const HeroNote = styled.p`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 3rem;

  span { color: #a78bfa; font-weight: 700; }
`;

const ApplyButton = styled(motion.button)`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1.3rem 3.5rem;
  font-size: 1.25rem;
  font-weight: 900;
  color: #fff;
  background: linear-gradient(90deg, #a044ff, #6366f1, #00d2ff, #a044ff);
  background-size: 300% 100%;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  overflow: hidden;
  animation: ${moveGradient} 4s linear infinite, ${glowPulse} 3s ease-in-out infinite;
  transition: transform 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: -100%;
    width: 100%; height: 100%;
    background: linear-gradient(120deg, transparent, rgba(255,255,255,0.2), transparent);
    z-index: 1;
  }

  &:hover {
    transform: translateY(-4px) scale(1.04);
    &::before { animation: ${shine} 1.5s infinite; }
  }
`;

// ── Opening Banner ────────────────────────────────────────────
const OpeningBanner = styled.div`
  background: linear-gradient(135deg, rgba(79,70,229,0.18), rgba(160,85,247,0.12));
  border: 1px solid rgba(160, 85, 247, 0.4);
  border-radius: 2rem;
  padding: 1.8rem 2.5rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  box-shadow: 0 0 40px rgba(160, 85, 247, 0.12);
  position: relative;
  z-index: 1;

  .badge {
    margin-left: auto;
    background: rgba(160, 85, 247, 0.3);
    border: 1px solid rgba(160, 85, 247, 0.5);
    border-radius: 999px;
    padding: 0.4rem 1.2rem;
    color: #e9d5ff;
    font-weight: 900;
    font-size: 0.85rem;
    white-space: nowrap;
  }
`;

// ── Section ───────────────────────────────────────────────────
const Section = styled.section`
  padding: 120px 0;
  position: relative;
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;

  .bar { width: 50px; height: 3px; background: linear-gradient(90deg, #a044ff, transparent); }
  p { color: #a044ff; font-weight: 800; text-transform: uppercase; letter-spacing: 0.2em; margin: 0; font-size: 0.85rem; }
`;

// ── Program Cards ─────────────────────────────────────────────
const ProgramGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const ProgramCard = styled(motion.div)`
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 2.5rem;
  padding: 3rem;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; width: 100%; height: 4px;
    background: ${p => p.color || 'linear-gradient(90deg, #a044ff, #6366f1)'};
    opacity: 0.7;
  }

  &:hover {
    border-color: rgba(160, 68, 255, 0.3);
    transform: translateY(-8px);
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4), 0 0 30px rgba(160, 68, 255, 0.1);
  }

  .week {
    display: inline-block;
    padding: 4px 14px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 999px;
    font-size: 0.8rem;
    font-weight: 900;
    color: ${p => p.accentColor || '#a78bfa'};
    margin-bottom: 1.5rem;
  }

  h4 { font-size: 1.4rem; font-weight: 950; color: #fff; margin-bottom: 0.5rem; }
  .sub { color: ${p => p.accentColor || '#a78bfa'}; font-weight: 700; font-size: 0.95rem; margin-bottom: 1.5rem; }

  ul {
    padding: 0; margin: 0; list-style: none;
    li {
      color: #cbd5e1; font-size: 0.95rem; margin-bottom: 0.6rem;
      padding-left: 1.2rem; position: relative; font-weight: 500;
      &::before { content: '▹'; position: absolute; left: 0; color: ${p => p.accentColor || '#a78bfa'}; font-weight: 900; }
    }
  }
`;

// ── Info Cards ────────────────────────────────────────────────
const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-left: 4px solid ${p => p.accent || '#a044ff'};
  border-radius: 2rem;
  padding: 2.5rem;
  height: 100%;

  h5 {
    display: flex; align-items: center; gap: 0.6rem;
    font-size: 1.2rem; font-weight: 900; color: #fff;
    margin-bottom: 1.8rem;
    svg { color: ${p => p.accent || '#a044ff'}; }
  }

  .info-row {
    display: flex; gap: 1.5rem; margin-bottom: 1rem; align-items: flex-start;
    .label { color: rgba(255,255,255,0.35); font-size: 0.8rem; font-weight: 700; text-transform: uppercase; min-width: 70px; padding-top: 2px; }
    .value { color: #fff; font-weight: 700; font-size: 0.95rem; line-height: 1.5; }
  }
`;

const AiToolRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.8rem 0;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  &:last-child { border-bottom: none; }
`;

const AiTag = styled.span`
  flex-shrink: 0;
  padding: 3px 12px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 900;
  margin-top: 2px;
  background: ${p => p.bg || 'rgba(255,255,255,0.08)'};
  border: ${p => p.border || '1px solid rgba(255,255,255,0.15)'};
  color: ${p => p.color || 'rgba(255,255,255,0.6)'};
`;

// ── App Banner ────────────────────────────────────────────────
const AppBanner = styled.div`
  background: linear-gradient(135deg, rgba(251,191,36,0.1), rgba(245,158,11,0.07));
  border: 1px solid rgba(251, 191, 36, 0.35);
  border-left: 5px solid #fbbf24;
  border-radius: 1.5rem;
  padding: 1.8rem 2rem;
  display: flex;
  align-items: flex-start;
  gap: 1.2rem;
  margin-top: 2rem;

  .icon { font-size: 2rem; line-height: 1; flex-shrink: 0; }
  .title { color: #fde68a; font-weight: 900; font-size: 1rem; margin-bottom: 6px; }
  .desc { color: rgba(255,255,255,0.65); font-size: 0.9rem; line-height: 1.7; }
  strong { color: #fff; }
  em { color: #fde68a; font-style: normal; font-weight: 700; }
`;

// ── Example Flow ──────────────────────────────────────────────
const ExampleSection = styled.section`
  padding: 100px 0;
  position: relative;
`;

const ExampleFlowWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  flex-wrap: wrap;
  margin-top: 3.5rem;

  @media (max-width: 768px) { flex-direction: column; gap: 0; }
`;

const FlowStep = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 160px;

  .icon-wrap {
    width: 72px; height: 72px;
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 2rem;
    background: ${p => p.bg || 'rgba(160,68,255,0.12)'};
    border: 2px solid ${p => p.border || 'rgba(160,68,255,0.35)'};
    box-shadow: 0 0 24px ${p => p.glow || 'rgba(160,68,255,0.2)'};
    margin-bottom: 1rem;
  }
  .step-title { color: #fff; font-weight: 800; font-size: 0.9rem; margin-bottom: 0.35rem; line-height: 1.4; }
  .step-desc  { color: rgba(255,255,255,0.4); font-size: 0.78rem; line-height: 1.5; }

  @media (max-width: 768px) { width: 100%; flex-direction: row; gap: 1.2rem; text-align: left; padding: 0.5rem 0; }
`;

const FlowArrow = styled.div`
  color: rgba(160,68,255,0.5);
  font-size: 1.5rem;
  padding: 0 0.5rem;
  margin-bottom: 2rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    transform: rotate(90deg);
    margin: 0.3rem auto;
    padding: 0;
  }
`;

const ExampleResultCard = styled(motion.div)`
  margin-top: 3.5rem;
  background: linear-gradient(135deg, rgba(30,15,60,0.7), rgba(10,25,60,0.7));
  border: 1px solid rgba(160,68,255,0.25);
  border-radius: 2rem;
  padding: 2.5rem 3rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; height: 3px;
    background: linear-gradient(90deg, #a044ff, #00d2ff, #a044ff);
    background-size: 200% 100%;
    animation: ${moveGradient} 4s linear infinite;
  }

  .email-header {
    display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem;
    .dot-row { display: flex; gap: 6px; }
    .dot { width: 10px; height: 10px; border-radius: 50%; }
  }
  .email-meta { margin-bottom: 1.2rem; }
  .meta-row { display: flex; gap: 1rem; margin-bottom: 0.4rem; font-size: 0.85rem; }
  .meta-label { color: rgba(255,255,255,0.3); min-width: 50px; }
  .meta-val { color: rgba(255,255,255,0.75); }
  .email-body { border-top: 1px solid rgba(255,255,255,0.07); padding-top: 1.2rem; }
  .news-item {
    display: flex; gap: 1rem; align-items: flex-start;
    padding: 0.9rem 0; border-bottom: 1px solid rgba(255,255,255,0.05);
    &:last-child { border-bottom: none; }
    .news-num { color: #a044ff; font-weight: 900; font-size: 0.85rem; min-width: 20px; }
    .news-content {}
    .news-title { color: #fff; font-weight: 700; font-size: 0.92rem; margin-bottom: 0.25rem; }
    .news-summary { color: rgba(255,255,255,0.45); font-size: 0.82rem; line-height: 1.6; }
  }
`;

// ── AI Employee Road ──────────────────────────────────────────
const RoadmapSection = styled.section`
  padding: 80px 0 100px;
  position: relative;
`;

const RoadmapGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 60px 1fr;
  gap: 0;
  align-items: center;
  margin-top: 3.5rem;

  @media (max-width: 768px) { grid-template-columns: 1fr; gap: 2rem; }
`;

const RoadmapCard = styled(motion.div)`
  background: ${p => p.featured
    ? 'linear-gradient(135deg, rgba(160,68,255,0.15), rgba(0,210,255,0.08))'
    : 'rgba(15,23,42,0.4)'};
  border: 1px solid ${p => p.featured ? 'rgba(160,68,255,0.4)' : 'rgba(255,255,255,0.07)'};
  border-radius: 2.5rem;
  padding: 3rem 2.5rem;
  position: relative;
  overflow: hidden;

  ${p => p.featured && `
    box-shadow: 0 0 60px rgba(160,68,255,0.12);
  `}

  .card-badge {
    display: inline-flex; align-items: center; gap: 0.5rem;
    padding: 0.35rem 1rem;
    border-radius: 999px;
    font-size: 0.75rem; font-weight: 900;
    background: ${p => p.featured ? 'rgba(160,68,255,0.25)' : 'rgba(255,255,255,0.06)'};
    border: 1px solid ${p => p.featured ? 'rgba(160,68,255,0.5)' : 'rgba(255,255,255,0.1)'};
    color: ${p => p.featured ? '#c4b5fd' : 'rgba(255,255,255,0.4)'};
    margin-bottom: 1.5rem;
  }

  h3 {
    font-size: 1.5rem; font-weight: 950; color: #fff;
    margin-bottom: 0.7rem; letter-spacing: -0.02em;
  }
  .card-desc {
    color: rgba(255,255,255,0.5); font-size: 0.95rem; line-height: 1.7;
    margin-bottom: 1.8rem;
  }

  ul {
    padding: 0; margin: 0; list-style: none;
    li {
      color: #cbd5e1; font-size: 0.9rem; margin-bottom: 0.7rem;
      padding-left: 1.3rem; position: relative; font-weight: 500; line-height: 1.5;
      &::before { content: '▹'; position: absolute; left: 0; color: ${p => p.featured ? '#a044ff' : 'rgba(255,255,255,0.3)'}; }
    }
  }

  .highlight-box {
    margin-top: 1.8rem;
    background: rgba(0,0,0,0.25);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 1rem;
    padding: 1rem 1.2rem;
    font-size: 0.85rem; color: rgba(255,255,255,0.5); line-height: 1.7;
    em { color: ${p => p.featured ? '#a78bfa' : '#00d2ff'}; font-style: normal; font-weight: 700; }
  }
`;

const RoadmapConnector = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 0.5rem;
  color: rgba(160,68,255,0.5);
  font-size: 1.8rem;

  .connector-label {
    font-size: 0.7rem; font-weight: 900;
    color: rgba(255,255,255,0.2);
    letter-spacing: 0.1em; text-transform: uppercase;
    writing-mode: vertical-rl;
  }

  @media (max-width: 768px) {
    flex-direction: row; justify-content: center;
    .connector-label { writing-mode: horizontal-tb; }
  }
`;

// ── Static data ───────────────────────────────────────────────
const programs = [
  {
    id: 1, week: '울산 19:00~22:00 · 서울 오전/오후 택 1', title: 'AI 직원 설계 · 구현 완성',
    sub: '한 타임 안에 설계부터 실제 투입까지 완성',
    color: 'linear-gradient(90deg, #1a73e8, #a044ff)', accentColor: '#818cf8',
    details: ['Claude로 내 반복 업무 분석 및 AI 직원 역할 설계', `${aiToolsInfo.tools.join(' + ')}로 나만의 자동화 루틴 직접 구현`, '나니아 AI 서버에서 24시간 상시 작동 설정'],
  },
  {
    id: 2, week: '이후 1개월: 자유 이용', title: '만든 AI 직원, 계속 운용하기',
    sub: '나니아 서비스 무제한 이용',
    color: 'linear-gradient(90deg, #00d2ff, #3a7bd5)', accentColor: '#00d2ff',
    details: ['나니아 AI 서버 1개월 무제한 이용권 제공', 'AI 직원 지속 개선 및 업무 자동화 확장', '카카오 채널로 코칭 및 관리'],
  },
];

const codeSamples = [
  'ai_employee.start()', `${aiToolsInfo.tools[0].toLowerCase()}.plan()`, 'claude.work(24/7)', 'gemini.run_task()',
  'await ai.do_my_job()', 'auto_reply.enable()', 'narnia.deploy(agent)',
  'schedule("daily", task)', 'ai_worker.loop()',
];

const dataLines = [
  { left: '8%',  height: '180px', duration: '4.2s', delay: '0s',   color: '#a044ff' },
  { left: '17%', height: '130px', duration: '3.5s', delay: '1.2s', color: '#818cf8' },
  { left: '29%', height: '200px', duration: '5.1s', delay: '0.4s', color: '#a044ff' },
  { left: '43%', height: '150px', duration: '3.8s', delay: '2.1s', color: '#00d2ff' },
  { left: '55%', height: '170px', duration: '4.6s', delay: '0.8s', color: '#818cf8' },
  { left: '67%', height: '120px', duration: '3.2s', delay: '1.7s', color: '#a044ff' },
  { left: '78%', height: '210px', duration: '5.4s', delay: '0.2s', color: '#00d2ff' },
  { left: '88%', height: '160px', duration: '4.0s', delay: '2.5s', color: '#a044ff' },
  { left: '94%', height: '140px', duration: '3.6s', delay: '1.0s', color: '#818cf8' },
  { left: '3%',  height: '190px', duration: '4.8s', delay: '3.2s', color: '#00d2ff' },
];

const codePositions = [
  { left: '5%',  top: '15%', duration: '14s', delay: '0s'  },
  { left: '20%', top: '70%', duration: '18s', delay: '2s'  },
  { left: '38%', top: '30%', duration: '12s', delay: '5s'  },
  { left: '55%', top: '60%', duration: '16s', delay: '1s'  },
  { left: '70%', top: '20%', duration: '20s', delay: '3s'  },
  { left: '82%', top: '75%', duration: '13s', delay: '7s'  },
  { left: '92%', top: '45%', duration: '17s', delay: '4s'  },
  { left: '45%', top: '85%', duration: '15s', delay: '6s'  },
  { left: '30%', top: '48%', duration: '19s', delay: '8s'  },
];

// ── Component ─────────────────────────────────────────────────
const AdultPage = () => {
  const [particlesReady, setParticlesReady] = useState(false);

  const particlesInit = async (main) => { await loadFull(main); };
  const particlesLoaded = async () => { setTimeout(() => setParticlesReady(true), 100); };

  return (
    <Page>
      <AdultHeader />
      {/* ─── Hero ─────────────────────────────────────────── */}
      <HeroSection>
        <BackgroundLayer>
          {/* Particles */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: particlesReady ? 1 : 0 }}
            transition={{ duration: 2 }}
            style={{
              position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0,
              maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 60%, rgba(0,0,0,0) 85%)',
              WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 60%, rgba(0,0,0,0) 85%)',
            }}
          >
            <Particles
              id="tsparticles-adult"
              init={particlesInit}
              loaded={particlesLoaded}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
              options={{
                fullScreen: { enable: false },
                particles: {
                  number: { value: 55, density: { enable: true, area: 1000 } },
                  color: { value: ['#a044ff', '#818cf8', '#00d2ff'] },
                  shape: { type: 'circle' },
                  opacity: { value: { min: 0.08, max: 0.35 } },
                  size: { value: { min: 1, max: 2.5 } },
                  links: {
                    enable: true, distance: 180,
                    color: '#a044ff', opacity: 0.1, width: 1,
                  },
                  move: {
                    enable: true, speed: 0.5, direction: 'none',
                    random: true, straight: false, outModes: { default: 'out' },
                  },
                },
                interactivity: {
                  events: { onHover: { enable: true, mode: 'grab' }, onClick: { enable: false } },
                  modes: { grab: { distance: 220, links: { opacity: 0.4 } } },
                },
                detectRetina: true,
              }}
            />
          </motion.div>

          <Aurora />
          <NeonGrid />
          <CRTOverlay />

          {/* Data Streams */}
          {dataLines.map((d, i) => (
            <DataLine key={i} left={d.left} height={d.height} duration={d.duration} delay={d.delay} color={d.color} />
          ))}

          {/* Floating Code */}
          {codeSamples.map((code, i) => (
            <CodeFragment
              key={i}
              left={codePositions[i].left}
              top={codePositions[i].top}
              duration={codePositions[i].duration}
              delay={codePositions[i].delay}
            >
              {code}
            </CodeFragment>
          ))}
        </BackgroundLayer>

        <Container style={{ position: 'relative', zIndex: 10 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: 'easeOut' }}
          >
            <HeroLabel>
              <FiStar size={14} /> Narnia Adult · AI 직원 육성 과정
            </HeroLabel>

            <HeroTitle>
              나 대신 일하는<br />
              <span className="accent">AI 직원을 만듭니다</span>
            </HeroTitle>

            <HeroSub>
              <strong>{aiToolsInfo.sentence}</strong>가 24시간 쉬지 않고<br />
              내 업무를 처리하는 AI 직원이 됩니다.
            </HeroSub>

            <HeroNote>
              코딩 몰라도 됩니다. AI 직원을 <span>설계하는 것</span>이 당신의 역할입니다.
            </HeroNote>

            <ApplyButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => window.location.href = '/apply#public-event-apply'}
            >
              지금 신청하기 <FiArrowRight />
            </ApplyButton>
          </motion.div>
        </Container>
      </HeroSection>

      {/* ─── 개강 배너 ────────────────────────────────────── */}
      <Section id="adult-schedule" style={{ paddingTop: '60px', paddingBottom: '80px' }}>
        <Container>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <OpeningBanner>
              <span style={{ fontSize: '2rem' }}>📅</span>
              <div>
                <div style={{ color: '#c4b5fd', fontWeight: 900, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '0.3rem' }}>신규 성인반 첫 개강</div>
                <div style={{ color: '#fff', fontWeight: 900, fontSize: '1.2rem' }}>
                  2025년 4월 22일 (수) · 울산 <span style={{ color: '#a5b4fc' }}>타워더모스트 팀솔루션</span>
                </div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem', marginTop: '0.3rem' }}>
                  19:00 ~ 22:00 · 1개월 나니아 서비스 이용
                </div>
              </div>
              <div className="badge">선착순 마감</div>
            </OpeningBanner>
            <OpeningBanner style={{ marginTop: '1.25rem' }}>
              <span style={{ fontSize: '2rem' }}>📅</span>
              <div>
                <div style={{ color: '#c4b5fd', fontWeight: 900, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '0.3rem' }}>신규 성인반 개강</div>
                <div style={{ color: '#fff', fontWeight: 900, fontSize: '1.2rem' }}>
                  2025년 4월 25일 (토) · 서울 이태원 <span style={{ color: '#a5b4fc' }}>한국폴리텍대학</span>
                </div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem', marginTop: '0.3rem' }}>
                  오전 09:00~12:00 · 오후 18:00~21:00 중 택 1 · 1개월 나니아 서비스 이용
                </div>
              </div>
              <div className="badge">선착순 마감</div>
            </OpeningBanner>
          </motion.div>
        </Container>
      </Section>

      {/* ─── 과정 구성 ────────────────────────────────────── */}
      <Section id="adult-program" style={{ paddingTop: '60px' }}>
        <Container>
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} style={{ marginBottom: '4rem' }}>
            <SectionLabel><div className="bar" /><p>Program</p></SectionLabel>
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 950, letterSpacing: '-0.03em', marginBottom: '1rem' }}>
              수업 구성
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '1.1rem', fontWeight: 500, lineHeight: 1.7 }}>
              울산은 저녁 타임(19:00~22:00), 서울은 오전(09:00~12:00) 또는 오후(18:00~21:00) 중 원하는 시간을 선택하세요.<br />
              한 타임 안에 AI 직원 설계부터 실제 구현 · 투입까지 모두 완성합니다.
            </p>
          </motion.div>

          <ProgramGrid>
            {programs.map((p, i) => (
              <ProgramCard
                key={p.id}
                color={p.color}
                accentColor={p.accentColor}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="week">{p.week}</div>
                <h4>{p.title}</h4>
                <div className="sub">{p.sub}</div>
                <ul>{p.details.map((d, idx) => <li key={idx}>{d}</li>)}</ul>
              </ProgramCard>
            ))}
          </ProgramGrid>
        </Container>
      </Section>

      {/* ─── 기초 예제: 뉴스 자동 메일 ──────────────────── */}
      <ExampleSection id="adult-example">
        <Container>
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} style={{ marginBottom: '1rem' }}>
            <SectionLabel><div className="bar" /><p>기초 실습 예제</p></SectionLabel>
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 950, letterSpacing: '-0.03em', marginBottom: '1rem' }}>
              오늘 당장 만드는 것
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '1.1rem', fontWeight: 500, lineHeight: 1.7, maxWidth: '640px' }}>
              수업 중 기초 예제로 <strong style={{ color: '#fff' }}>AI가 매일 관심 기사를 찾아서 내 메일로 자동 발송</strong>하는
              시스템을 직접 만듭니다. 코딩 없이, Claude가 모든 코드를 작성합니다.
            </p>
          </motion.div>

          {/* 플로우 다이어그램 */}
          <ExampleFlowWrap>
            {[
              { icon: '🔑', title: '관심 키워드 설정', desc: '업계·관심사 키워드를 Claude에게 알려줍니다', bg: 'rgba(160,68,255,0.12)', border: 'rgba(160,68,255,0.4)', glow: 'rgba(160,68,255,0.2)' },
              { icon: '🔍', title: 'AI 자동 검색', desc: '매일 정해진 시각, AI가 최신 기사를 수집합니다', bg: 'rgba(99,102,241,0.12)', border: 'rgba(99,102,241,0.4)', glow: 'rgba(99,102,241,0.2)' },
              { icon: '✂️', title: '요약 & 큐레이션', desc: '핵심만 3줄로 정리해 불필요한 정보를 제거합니다', bg: 'rgba(0,180,255,0.1)', border: 'rgba(0,210,255,0.35)', glow: 'rgba(0,210,255,0.15)' },
              { icon: '📧', title: '메일 자동 발송', desc: '매일 아침 정리된 뉴스레터가 메일함에 도착합니다', bg: 'rgba(16,185,129,0.1)', border: 'rgba(16,185,129,0.35)', glow: 'rgba(16,185,129,0.15)' },
            ].map((step, i) => (
              <React.Fragment key={i}>
                <FlowStep
                  bg={step.bg} border={step.border} glow={step.glow}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.12 }}
                >
                  <div className="icon-wrap">{step.icon}</div>
                  <div>
                    <div className="step-title">{step.title}</div>
                    <div className="step-desc">{step.desc}</div>
                  </div>
                </FlowStep>
                {i < 3 && <FlowArrow>→</FlowArrow>}
              </React.Fragment>
            ))}
          </ExampleFlowWrap>

          {/* 결과물 미리보기: 메일 목업 */}
          <ExampleResultCard
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: 0.2 }}
          >
            <div className="email-header">
              <div className="dot-row">
                <div className="dot" style={{ background: '#ff5f56' }} />
                <div className="dot" style={{ background: '#ffbd2e' }} />
                <div className="dot" style={{ background: '#27c93f' }} />
              </div>
              <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.8rem', fontFamily: 'Fira Code, monospace' }}>
                inbox · my@email.com
              </span>
            </div>
            <div className="email-meta">
              <div className="meta-row"><span className="meta-label">발신</span><span className="meta-val">AI 뉴스레터 직원 &lt;ai-worker@narnia&gt;</span></div>
              <div className="meta-row"><span className="meta-label">수신</span><span className="meta-val">나 &lt;my@email.com&gt;</span></div>
              <div className="meta-row"><span className="meta-label">제목</span><span className="meta-val" style={{ color: '#fff', fontWeight: 700 }}>📰 오늘의 AI 업계 소식 — 2026년 4월 16일</span></div>
            </div>
            <div className="email-body">
              {[
                { title: 'OpenAI, GPT-5 공개 임박… 추론 능력 획기적 개선', summary: 'OpenAI가 다음 달 GPT-5를 정식 출시한다고 밝혔습니다. 내부 테스트에서 수학·코딩 벤치마크 기록을 크게 경신했으며, 멀티모달 처리 속도도 3배 향상된 것으로 알려졌습니다.' },
                { title: '국내 스타트업, AI 직원 도입 후 업무 처리 속도 40% 증가', summary: 'AI 자동화를 도입한 중소기업 50곳을 대상으로 한 조사 결과, 반복 업무 처리 시간이 평균 40% 단축된 것으로 나타났습니다. 특히 이메일 응대·보고서 작성 분야에서 효과가 두드러졌습니다.' },
                { title: 'Claude API 업데이트: 더 긴 컨텍스트·저렴한 가격으로 접근성 확대', summary: '앤트로픽이 Claude API의 컨텍스트 창을 200K 토큰으로 확장하고 가격을 20% 인하했습니다. 중소기업의 AI 도입 장벽이 낮아질 것으로 기대됩니다.' },
              ].map((item, i) => (
                <div className="news-item" key={i}>
                  <div className="news-num">0{i + 1}</div>
                  <div className="news-content">
                    <div className="news-title">{item.title}</div>
                    <div className="news-summary">{item.summary}</div>
                  </div>
                </div>
              ))}
            </div>
          </ExampleResultCard>

          <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: '0.82rem', marginTop: '1.5rem' }}>
            ↑ 수업 중, 이런 자동 메일이 매일 발송되는 시스템을 직접 만듭니다
          </p>
        </Container>
      </ExampleSection>

      {/* ─── AI 직원 로드맵 ──────────────────────────────── */}
      <RoadmapSection id="adult-roadmap">
        <Container>
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} style={{ marginBottom: '1rem' }}>
            <SectionLabel><div className="bar" /><p>Curriculum · 수업 흐름</p></SectionLabel>
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 950, letterSpacing: '-0.03em', marginBottom: '1rem' }}>
              기초에서 나만의 AI 직원까지
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '1.1rem', fontWeight: 500, lineHeight: 1.7, maxWidth: '640px' }}>
              뉴스 메일 자동화는 시작에 불과합니다. 같은 원리로 <strong style={{ color: '#fff' }}>Claude를 내 업무에 맞게 설계</strong>하면,
              24시간 쉬지 않는 진짜 AI 직원이 만들어집니다.
            </p>
          </motion.div>

          <RoadmapGrid>
            {/* 기초 카드 */}
            <RoadmapCard
              initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ delay: 0.05 }}
            >
              <div className="card-badge">📌 기초 실습 예제</div>
              <h3>AI 뉴스레터 직원</h3>
              <p className="card-desc">
                Claude가 매일 인터넷을 검색하고, 관심 기사를 요약해서 내 메일로 자동 발송합니다.
                이 예제를 통해 AI 자동화의 핵심 원리를 익힙니다.
              </p>
              <ul>
                <li>키워드 기반 기사 자동 수집 설정</li>
                <li>Claude로 요약·큐레이션 프롬프트 작성</li>
                <li>스케줄러로 매일 자동 실행 연결</li>
                <li>이메일 발송 자동화 완성</li>
              </ul>
              <div className="highlight-box">
                <em>결과물:</em> 매일 아침 원하는 키워드 뉴스가 내 메일함에 자동 도착
              </div>
            </RoadmapCard>

            {/* 화살표 커넥터 */}
            <RoadmapConnector>
              <span>→</span>
              <span className="connector-label">심화</span>
            </RoadmapConnector>

            {/* 심화 카드 */}
            <RoadmapCard
              featured
              initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ delay: 0.1 }}
            >
              <div className="card-badge">⚡ 심화 · 나만의 AI 직원</div>
              <h3>Claude 24시간 AI 직원</h3>
              <p className="card-desc">
                뉴스레터에서 배운 구조를 확장해, 내 실제 업무를 처리하는 Claude AI 직원을 설계·배포합니다.
                나니아 서버에서 24시간 상시 작동합니다.
              </p>
              <ul>
                <li>내 반복 업무 분석 및 AI 직원 역할 정의</li>
                <li>Claude로 나만의 업무 자동화 루틴 구현</li>

                <li>나니아 서버에서 24시간 무중단 운용</li>
                <li>1개월 지속 개선 및 업무 자동화 확장</li>
              </ul>
              <div className="highlight-box">
                <em>결과물:</em> 보고서 초안 작성, 고객 응대, 일정 정리 등 실제 업무를 처리하는 나만의 AI 직원
              </div>
            </RoadmapCard>
          </RoadmapGrid>

          {/* 확장 가능 예시 칩들 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: 0.15 }}
            style={{ marginTop: '3rem', textAlign: 'center' }}
          >
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.85rem', marginBottom: '1rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              이런 AI 직원을 만들 수 있습니다
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', justifyContent: 'center' }}>
              {[
                '📰 매일 뉴스 요약 & 메일 발송',
                '📊 주간 업무 보고서 자동 초안',
                '💬 고객 문의 1차 자동 응대',
                '📅 회의 내용 요약 & 할일 추출',
                '🔍 경쟁사 동향 자동 모니터링',
                '📈 데이터 분석 & 인사이트 리포트',
                '✉️ 이메일 초안 자동 작성',
                '🛒 가격 변동 알림 자동화',
              ].map((item, i) => (
                <span key={i} style={{
                  padding: '0.45rem 1rem',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '999px',
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                }}>
                  {item}
                </span>
              ))}
            </div>
          </motion.div>
        </Container>
      </RoadmapSection>

      {/* ─── 수강 정보 + AI 도구 ──────────────────────────── */}
      <Section id="adult-info" style={{ paddingTop: '40px' }}>
        <Container>
          {/* 울산 / 서울 — 2열 대칭 */}
          <Row className="g-4 mb-4" style={{ alignItems: 'stretch' }}>
            <Col lg={6} style={{ display: 'flex' }}>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ width: '100%' }}>
                <InfoCard accent="#f59e0b" style={{ height: '100%' }}>
                  <h5><FiCalendar /> 울산 오프라인 수업</h5>
                  <div className="info-row">
                    <span className="label">Date</span>
                    <span className="value">2025년 4월 22일 (수)</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Venue</span>
                    <span className="value">울산 타워더모스트 팀솔루션</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Time</span>
                    <span className="value">🌙 19:00 ~ 22:00 (3시간)</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Target</span>
                    <span className="value">직장인 · 프리랜서 · 예비 창업자</span>
                  </div>
                  <div className="info-row">
                    <span className="label">준비물</span>
                    <span className="value">노트북 (웹캠·마이크 불필요)</span>
                  </div>
                </InfoCard>
              </motion.div>
            </Col>
            <Col lg={6} style={{ display: 'flex' }}>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} style={{ width: '100%' }}>
                <InfoCard accent="#00d2ff" style={{ height: '100%' }}>
                  <h5><FiCalendar /> 서울 오프라인 수업</h5>
                  <div className="info-row">
                    <span className="label">Date</span>
                    <span className="value">2025년 4월 25일 (토)</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Venue</span>
                    <span className="value">서울 이태원 한국폴리텍대학</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Time</span>
                    <span className="value" style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      <span>🌅 오전 타임&nbsp;&nbsp;09:00 ~ 12:00</span>
                      <span>🌆 오후 타임&nbsp;&nbsp;18:00 ~ 21:00</span>
                      <span style={{ color: 'rgba(255,255,255,0.38)', fontSize: '0.8rem' }}>※ 오전 / 오후 중 택 1 · 각 3시간</span>
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="label">Target</span>
                    <span className="value">직장인 · 프리랜서 · 예비 창업자</span>
                  </div>
                  <div className="info-row">
                    <span className="label">준비물</span>
                    <span className="value">노트북 (웹캠·마이크 불필요)</span>
                  </div>
                </InfoCard>
              </motion.div>
            </Col>
          </Row>

          {/* AI 도구 안내 — 별도 전체 폭 */}
          <Row className="g-4">
            <Col lg={12}>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
                <InfoCard accent="#a044ff">
                  <h5><FiZap /> AI 도구 안내</h5>
                  <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.9rem', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                    수업에서는 <strong style={{ color: '#fff' }}>{aiToolsInfo.sentence}</strong> 세 AI를 모두 활용할 수 있습니다.
                  </p>
                  <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                    <AiToolRow style={{ flex: 1, minWidth: '240px' }}>
                      <AiTag bg="linear-gradient(135deg,#10a37f,#22c55e)" color="#fff" border="none">활용 가능</AiTag>
                      <div>
                        <div style={{ color: '#fff', fontWeight: 800, fontSize: '0.95rem' }}>ChatGPT</div>
                        <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem', marginTop: '2px' }}>기획 · 코드 작성 · 문제 해결에 활용</div>
                      </div>
                    </AiToolRow>
                    <AiToolRow style={{ flex: 1, minWidth: '240px' }}>
                      <AiTag bg="linear-gradient(135deg,#1a73e8,#4285f4)" color="#fff" border="none">무료 제공</AiTag>
                      <div>
                        <div style={{ color: '#fff', fontWeight: 800, fontSize: '0.95rem' }}>Gemini Pro</div>
                        <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem', marginTop: '2px' }}>나니아 서비스 내 기본 제공</div>
                      </div>
                    </AiToolRow>
                    <AiToolRow style={{ flex: 1, minWidth: '240px' }}>
                      <AiTag>개별 결제</AiTag>
                      <div>
                        <div style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 800, fontSize: '0.95rem' }}>Claude</div>
                        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', marginTop: '2px', lineHeight: 1.5 }}>
                          최소 Pro 요금제 구독 필요<br />
                          <a href="https://claude.ai/upgrade?from=menu" target="_blank" rel="noopener noreferrer" style={{ color: '#a78bfa', fontWeight: 700, fontSize: '0.82rem' }}>
                            claude.ai/upgrade →
                          </a>
                        </div>
                      </div>
                    </AiToolRow>
                  </div>
                </InfoCard>
              </motion.div>
            </Col>
          </Row>

          {/* 신청 안내 */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}>
            <AppBanner>
              <div className="icon">✦</div>
              <div>
                <div className="title">나니아데이 공개 일정은 이 페이지에서 바로 신청할 수 있습니다.</div>
                <div className="desc">
                  <strong>계정 정보와 약관 동의를 입력하면 계정 생성과 신청이 함께 접수됩니다.</strong><br />
                  신청 내역은 계정과 비밀번호로 확인하거나 취소할 수 있고,<br />
                  담당자가 CRM에서 확인한 뒤 필요한 안내를 이어갑니다.
                </div>
              </div>
            </AppBanner>
          </motion.div>


          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            style={{ textAlign: 'center', marginTop: '5rem' }}
          >
            <ApplyButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => window.location.href = '/apply#public-event-apply'}
              style={{ fontSize: '1.3rem', padding: '1.4rem 4rem' }}
            >
              지금 신청하기 <FiArrowRight />
            </ApplyButton>
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.85rem', marginTop: '1.2rem' }}>
              선착순 마감 · 4월 22일(수) 울산 타워더모스트 팀솔루션 19:00~22:00 · 4월 25일(토) 서울 이태원 한국폴리텍대학 오전/오후 택 1
            </p>
          </motion.div>
        </Container>
      </Section>

      {/* ─── Footer ───────────────────────────────────────── */}
      <footer style={{ padding: '4rem 0', borderTop: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
        <Container>
          <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.85rem', margin: 0 }}>
            © 2025 Narnia Lab. All rights reserved.
          </p>
          <p style={{ color: 'rgba(255,255,255,0.15)', fontSize: '0.8rem', marginTop: '0.5rem' }}>
            <a href="/apply" style={{ color: 'rgba(255,255,255,0.25)' }}>청소년 과정 보기 →</a>
          </p>
        </Container>
      </footer>

      {/* ─── Floating Buttons ─────────────────────────────── */}
      <SocialFloatingButton
        href="https://www.instagram.com/narnialab_official?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw=="
        icon={FaInstagram}
        text="인스타그램"
        bottom="2rem" right="2rem"
        background="linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)"
        textColor="white"
        zIndex={1001}
        ariaLabel="인스타그램 바로가기"
        openInNewTab={true}
      />
      <NarniaTalkWidget />
    </Page>
  );
};

export default AdultPage;
